import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PricingCard from '../components/PricingCard';
import BrandIcon from '../components/BrandIcon';
import FaqStructuredData from '../components/FaqStructuredData';
import useAssetDetector from '../hooks/useAssetDetector';
import { handleTalkToSpecialist } from '../utils/webchat';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';

// ── Asset helpers ─────────────────────────────────────────────────────────────
const HERO_VIDEO_MP4  = '/assets/videos/hero-demo.mp4';
const HERO_VIDEO_WEBM = '/assets/videos/hero-demo.webm';
const HOW_VIDEO_MP4   = '/assets/videos/how-it-works.mp4';
const HOW_VIDEO_WEBM  = '/assets/videos/how-it-works.webm';

const SCREENSHOTS = {
  dashboard:    '/assets/screenshots/screenshot-dashboard.png',
  agentConfig:  '/assets/screenshots/screenshot-agent-config.png',
  callcenter:   '/assets/screenshots/screenshot-callcenter.png',
  campaigns:    '/assets/screenshots/screenshot-campaigns.png',
};

// ── Fallbacks ─────────────────────────────────────────────────────────────────
// Fallback exibido quando a API /public/connector-types está indisponível.
// As chaves seguem as categorias canônicas do backend
// (backend/src/constants/connector-categories.js).
const FALLBACK_INTEGRATIONS = {
  communication: [{ name: 'WhatsApp Business', icon: 'si:whatsapp' }, { name: 'E-mail', icon: '📧' }, { name: 'Telegram', icon: 'si:telegram' }, { name: 'Instagram Direct', icon: 'si:instagram' }, { name: 'Webchat', icon: '🌐' }, { name: 'SMS', icon: '📱' }, { name: 'Amazon Alexa', icon: '🔊' }],
  healthcare:    [{ name: 'Feegow', icon: '🏥' }, { name: 'Naja', icon: '🩺' }],
  crm:           [{ name: 'RD Station', icon: 'si:rdstation' }, { name: 'HubSpot', icon: 'si:hubspot' }],
  finance:       [{ name: 'Asaas', icon: '💳' }, { name: 'Stripe', icon: 'si:stripe' }, { name: 'Omie ERP', icon: '💼' }],
  productivity:  [{ name: 'Google Workspace', icon: 'si:google' }, { name: 'Notion', icon: 'si:notion' }, { name: 'Trello', icon: 'si:trello' }, { name: 'Microsoft 365', icon: 'si:microsoft' }],
  legal:         [{ name: 'ClickSign', icon: '✍️' }],
};

// Ordem canônica de exibição dos grupos (espelha CONNECTOR_CATEGORY_ORDER do backend)
const CATEGORY_ORDER = ['communication', 'healthcare', 'crm', 'finance', 'productivity', 'legal', 'developer'];

// Sem fallback de preços — se a API falhar, a seção não exibe valores incorretos

const fmtLimit = (v) => (v === -1 ? 'Ilimitados' : (v ?? '—'));

function normalizeTier(t, currency = 'BRL') {
  const price    = t.prices?.[currency]?.monthly ?? '—';
  const agents   = fmtLimit(t.limits?.agents);
  const channels = fmtLimit(t.limits?.channels);
  const members  = fmtLimit(t.limits?.members);
  const credits  = t.monthlyCredits === -1 ? -1 : (t.monthlyCredits > 0 ? t.monthlyCredits : null);
  // Mantém apenas as chaves de features ativas — a tradução dos rótulos é feita no PricingCard via i18n
  const featureKeys = Object.entries(t.features || {}).filter(([, v]) => v === true).map(([k]) => k);
  return { id: t.tierId, name: t.name, description: t.description || '', price: String(price), agents: String(agents), channels: String(channels), members: String(members), credits, featureKeys };
}

function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// Segmentos atendidos — card de Saúde em destaque com link dedicado
const SEGMENTS = [
  { key: 'health',     icon: '🩺', to: '/saude',    featured: true },
  { key: 'retail',     icon: '🛍️', to: '/agentes/categoria/vendas' },
  { key: 'education',  icon: '🎓', to: '/agentes/categoria/atendimento' },
  { key: 'finance',    icon: '💰', to: '/agentes/categoria/financeiro' },
  { key: 'services',   icon: '🛠️', to: '/agentes/categoria/operacoes' },
  { key: 'legal',      icon: '⚖️', to: '/agentes/categoria/juridico' },
];

// ── Sections ──────────────────────────────────────────────────────────────────

// Bolhas da conversa animada exibida no mockup do hero.
// Fluxo baseado em um atendimento real de agendamento (anonimizado): o agente
// identifica o paciente, consulta a disponibilidade e fecha a marcação.
const HERO_CHAT = [
  { role: 'user',  text: 'Bom dia! Gostaria de agendar uma consulta 😊' },
  { role: 'agent', text: 'Claro! Com qual especialista você prefere? Temos otorrinolaringologia e cirurgia de cabeça e pescoço.' },
  { role: 'user',  text: 'Otorrino, essa semana se possível' },
  { role: 'agent', text: 'Encontrei um horário 🎉 Quarta, 14/10 às 10:45 com a Dra. Marina. Esse horário te atende?' },
  { role: 'user',  text: 'Perfeito, pode confirmar' },
  { role: 'agent', text: '✅ Consulta confirmada para quarta, 14/10 às 10:45. Envio os detalhes e um lembrete no dia. Até lá!' },
];

function HeroChatMockup({ t }) {
  const [visible, setVisible] = useState(1);

  useEffect(() => {
    if (visible >= HERO_CHAT.length) return undefined;
    const timer = setTimeout(() => setVisible((v) => v + 1), 1300);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <div className="hero-mockup">
      <div className="hero-mockup-bar">
        <span className="hero-mockup-avatar">🩺</span>
        <div className="hero-mockup-id">
          <span className="hero-mockup-name">Alice · Recepção</span>
          <span className="hero-mockup-status">online agora</span>
        </div>
        <span className="hero-mockup-channel">💬 WhatsApp</span>
      </div>
      <div className="hero-mockup-body">
        {HERO_CHAT.slice(0, visible).map((m, i) => (
          <div key={i} className={`hero-bubble hero-bubble-${m.role}`}>{m.text}</div>
        ))}
        {visible < HERO_CHAT.length && (
          <div className="hero-bubble hero-bubble-agent hero-bubble-typing">
            <span className="demo-typing"><span /><span /><span /></span>
          </div>
        )}
      </div>
      <div className="hero-mockup-foot">{t('hero.mockupFoot')}</div>
    </div>
  );
}

function HeroSection({ t }) {
  const { exists: heroVideoExists } = useAssetDetector('/assets/videos/hero-demo.mp4');
  const trust = t('hero.trustChips', { returnObjects: true });

  return (
    <section className="hero">
      <div className="hero-bg-glow" aria-hidden="true" />
      <div className="hero-inner">
        <div className="hero-badge">{t('hero.badge')}</div>
        <h1 className="hero-title">
          {t('hero.title')}{' '}
          <span className="gradient-text">{t('hero.titleHighlight')}</span>
        </h1>
        <p className="hero-subtitle">{t('hero.subtitle')}</p>
        <div className="hero-actions">
          <button type="button" onClick={handleTalkToSpecialist} className="btn-hero-primary">{t('hero.cta')}</button>
          <a href="#segments" className="btn-hero-ghost">{t('hero.ctaSecondary')} →</a>
        </div>
        <p className="hero-note">{t('hero.trialNote')}</p>
        {Array.isArray(trust) && trust.length > 0 && (
          <ul className="hero-trust">
            {trust.map((chip, i) => (
              <li key={i} className="hero-trust-item">
                <span className="hero-trust-check" aria-hidden="true">✓</span>{chip}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="hero-visual">
        {heroVideoExists ? (
          <div className="hero-video-wrapper">
            <video
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
              poster="/assets/screenshots/screenshot-dashboard.png"
            >
              <source src={HERO_VIDEO_MP4} type="video/mp4" />
              <source src={HERO_VIDEO_WEBM} type="video/webm" />
            </video>
          </div>
        ) : (
          <HeroChatMockup t={t} />
        )}
      </div>
    </section>
  );
}

function MetricsSection({ t }) {
  const ref = useFadeIn();
  const metrics = t('metrics.items', { returnObjects: true });

  if (!Array.isArray(metrics)) return null;

  return (
    <section className="metrics-section fade-section" ref={ref}>
      <div className="metrics-inner">
        <div className="metrics-header">
          <h2>{t('metrics.title')}</h2>
          <p>{t('metrics.subtitle')}</p>
        </div>
        <div className="metrics-grid">
          {metrics.map((m, i) => (
            <div key={i} className="metric-card">
              <span className="metric-value">{m.value}</span>
              <span className="metric-label">{m.label}</span>
            </div>
          ))}
        </div>
        <p className="metrics-footnote">{t('metrics.footnote')}</p>
      </div>
    </section>
  );
}

function SegmentsSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="section fade-section" id="segments" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('segments.title')}</h2>
          <p>{t('segments.subtitle')}</p>
        </div>
        <div className="segments-grid">
          {SEGMENTS.map((seg) => {
            const label = t(`segments.items.${seg.key}.name`);
            const desc = t(`segments.items.${seg.key}.description`);
            const cta = seg.featured ? t('segments.featuredCta') : t('segments.defaultCta');
            return (
              <Link
                key={seg.key}
                to={seg.to}
                className={`segment-card ${seg.featured ? 'segment-card-featured' : ''}`}
              >
                {seg.featured && <span className="segment-badge">{t('segments.featuredBadge')}</span>}
                <span className="segment-icon" aria-hidden="true">{seg.icon}</span>
                <h3 className="segment-name">{label}</h3>
                <p className="segment-desc">{desc}</p>
                <span className="segment-cta">{cta} →</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UseCasesSection({ t }) {
  const ref = useFadeIn();
  const health = t('useCases.health', { returnObjects: true });
  const other = t('useCases.other', { returnObjects: true });
  if (!Array.isArray(health)) return null;

  return (
    <section className="section section-dark fade-section" id="use-cases" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('useCases.title')}</h2>
          <p>{t('useCases.subtitle')}</p>
        </div>

        <h3 className="usecase-group-label">{t('useCases.healthLabel')}</h3>
        <div className="usecases-grid">
          {health.map((item, i) => (
            <div key={i} className="usecase-card usecase-card-health">
              <span className="usecase-icon" aria-hidden="true">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </div>
          ))}
        </div>

        {Array.isArray(other) && other.length > 0 && (
          <>
            <h3 className="usecase-group-label usecase-group-label-muted">{t('useCases.otherLabel')}</h3>
            <div className="usecases-grid usecases-grid-other">
              {other.map((item, i) => (
                <div key={i} className="usecase-card">
                  <span className="usecase-icon" aria-hidden="true">{item.icon}</span>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function HowItWorksSection({ t }) {
  const ref = useFadeIn();
  const { exists: howVideoExists } = useAssetDetector(HOW_VIDEO_MP4);
  const { exists: agentConfigExists } = useAssetDetector(SCREENSHOTS.agentConfig);
  const { exists: dashboardExists } = useAssetDetector(SCREENSHOTS.dashboard);
  const steps = t('howItWorks.steps', { returnObjects: true });

  // Map step index to screenshot — Req 19.2: Step 2 → agent-config, Req 19.1: Step 3 → dashboard
  const stepScreenshots = [
    null,                                                          // Step 1 — no screenshot
    { src: SCREENSHOTS.agentConfig, exists: agentConfigExists },   // Step 2 — configurar prompt
    { src: SCREENSHOTS.dashboard,   exists: dashboardExists },     // Step 3 — conectar canais / dashboard
  ];

  return (
    <section className="section fade-section" id="how-it-works" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('howItWorks.title')}</h2>
          <p>{t('howItWorks.subtitle')}</p>
        </div>

        {howVideoExists ? (
          /* ── VÍDEO "COMO FUNCIONA" ── */
          <div className="how-video-wrapper">
            <video className="how-video" autoPlay loop muted playsInline poster={SCREENSHOTS.dashboard}>
              <source src={HOW_VIDEO_MP4} type="video/mp4" />
              <source src={HOW_VIDEO_WEBM} type="video/webm" />
            </video>
          </div>
        ) : (
          /* ── STEPS COM SCREENSHOTS (quando disponíveis) ou texto ── */
          <div className="steps-grid">
            {steps.map((step, i) => {
              const screenshot = stepScreenshots[i];
              return (
                <div key={step.number} className="step-card">
                  <div className="step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  {/* Screenshot aparece automaticamente quando o arquivo existir */}
                  {screenshot && screenshot.exists && (
                    <div className="step-screenshot-wrapper">
                      <img
                        src={screenshot.src}
                        alt={step.title}
                        className="step-screenshot"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturesSection({ t }) {
  const ref = useFadeIn();
  const items = t('features.items', { returnObjects: true });
  const { exists: callcenterExists } = useAssetDetector(SCREENSHOTS.callcenter);
  const { exists: campaignsExists } = useAssetDetector(SCREENSHOTS.campaigns);

  // Map feature index to screenshot — Req 19.3: index 1 (Call Center), Req 19.4: index 3 (Campaigns)
  const featureScreenshots = {
    1: { src: SCREENSHOTS.callcenter, exists: callcenterExists },
    3: { src: SCREENSHOTS.campaigns, exists: campaignsExists },
  };

  return (
    <section className="section section-dark fade-section" id="features" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('features.title')}</h2>
          <p>{t('features.subtitle')}</p>
        </div>
        <div className="features-grid">
          {items.map((item, i) => {
            const screenshot = featureScreenshots[i];
            return (
              <div key={i} className="feature-card">
                <div className="feature-icon"><BrandIcon icon={item.icon} size={32} /></div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                {/* Screenshot aparece automaticamente quando o arquivo existir (Req 19.5: hidden gracefully when absent) */}
                {screenshot && screenshot.exists && (
                  <img
                    src={screenshot.src}
                    alt={item.title}
                    className="feature-screenshot"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IntegrationsSection({ t }) {
  const ref = useFadeIn();
  const [grouped, setGrouped] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/public/connector-types`)
      .then(r => r.json())
      .then(data => {
        const types = data?.connectorTypes;
        if (!types?.length) { setGrouped(FALLBACK_INTEGRATIONS); return; }
        const groups = {};
        types.filter(ct => ct.category !== 'internal').forEach(ct => {
          const cat = ct.category || 'other';
          if (!groups[cat]) groups[cat] = [];
          groups[cat].push(ct);
        });
        setGrouped(groups);
      })
      .catch(() => setGrouped(FALLBACK_INTEGRATIONS));
  }, []);

  return (
    <section className="section section-dark fade-section" id="integrations" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('integrations.title')}</h2>
          <p>{t('integrations.subtitle')}</p>
        </div>
        {grouped === null ? (
          <div className="integrations-skeleton">
            {[...Array(8)].map((_, i) => <div key={i} className="integration-skeleton-card" />)}
          </div>
        ) : (
          <div className="integrations-categories">
            {Object.keys(grouped)
              .sort((a, b) => {
                const ia = CATEGORY_ORDER.indexOf(a);
                const ib = CATEGORY_ORDER.indexOf(b);
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
              })
              .map((cat) => (
                <div key={cat} className="integration-category">
                  <h3 className="integration-category-label">
                    {t(`integrations.categories.${cat}`, { defaultValue: cat })}
                  </h3>
                  <div className="integration-chips">
                    {grouped[cat].map(item => (
                      <div key={item.type || item.name} className="integration-chip">
                        {item.icon && <span className="integration-chip-icon"><BrandIcon icon={item.icon} size={18} /></span>}
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PricingSection({ t }) {
  const ref = useFadeIn();
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/public/tiers`)
      .then(r => r.json())
      .then(data => {
        const tiers = data?.tiers;
        setPlans(tiers?.length ? tiers.map(t => normalizeTier(t)) : []);
      })
      .catch(() => setPlans([]));
  }, []);

  return (
    <section className="section fade-section" id="pricing" ref={ref}>
      <div className="section-inner">
        <div className="section-header">
          <h2>{t('pricing.title')}</h2>
          <p>{t('pricing.subtitle')}</p>
        </div>
        {plans === null ? (
          <div className="pricing-skeleton">
            {[0, 1, 2].map(i => <div key={i} className="pricing-skeleton-card" />)}
          </div>
        ) : plans.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
            {t('pricing.unavailable', { defaultValue: 'Consulte nossos planos em app.libbenagents.com' })}
          </p>
        ) : (
          <div className="pricing-grid">
            {plans.map((plan, i) => (
              <PricingCard key={plan.id} plan={plan} highlighted={i === 2} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FaqSection({ t }) {
  const ref = useFadeIn();
  const [open, setOpen] = useState(null);
  const items = t('faq.items', { returnObjects: true });
  const toggle = useCallback((i) => setOpen(prev => prev === i ? null : i), []);

  return (
    <section className="section section-dark fade-section" id="faq" ref={ref}>
      <div className="section-inner">
        <div className="section-header"><h2>{t('faq.title')}</h2></div>
        <div className="faq-list" role="list">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={`faq-item ${isOpen ? 'open' : ''}`} role="listitem">
                <button className="faq-question" onClick={() => toggle(i)} aria-expanded={isOpen} aria-controls={`faq-panel-${i}`}>
                  {item.question}
                  <span className="faq-chevron" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>
                <div id={`faq-panel-${i}`} role="region" hidden={!isOpen}>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ t }) {
  const ref = useFadeIn();
  return (
    <section className="cta-section fade-section" ref={ref}>
      <div className="cta-inner">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.subtitle')}</p>
        <Link to="/saude#demo" className="btn-cta">{t('cta.button')}</Link>
        <p className="cta-note">{t('cta.note')}</p>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <title>Libben Agents — IA para Atendimento 24/7</title>
        <meta name="description" content={t('hero.subtitle')} />
        <meta property="og:title" content="Libben Agents — IA para Atendimento 24/7" />
        <meta property="og:description" content={t('hero.subtitle')} />
        <html lang={i18n.language} />
      </Helmet>
      <FaqStructuredData />
      <main>
        <HeroSection t={t} />
        <MetricsSection t={t} />
        <SegmentsSection t={t} />
        <HowItWorksSection t={t} />
        <FeaturesSection t={t} />
        <UseCasesSection t={t} />
        <IntegrationsSection t={t} />
        <PricingSection t={t} />
        <FaqSection t={t} />
        <CtaSection t={t} />
      </main>
    </>
  );
}
